import { useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  EntryList,
  Holidays,
  IsListView,
  IsLoading,
  IsSummaryLoading,
  IsRefreshing,
  HasInitiallyLoaded,
  ProjectList,
  Summary,
  TimeEntriesList,
  TimeEntriesSheetView,
  loadTimesInfo,
  updateSummaryOnly,
  setHolidays,
  HolidaysUserId,
  HolidaysRange,
  setHasPageError,
  setReminderData,
  setProjectList,
  SelectProjectOrWorkOrder,
  setLoading,
  setRefreshing,
  resetEntries,
  ScheduledHours,
  MissingTime,
  MissingTimeUserId,
  MissingTimeMealBreak,
  HasPageError,
} from "../redux/reducers/times.reducer";
import { TimesheetsService } from "../services/timesheets";
import { quantityFormat } from "../../SharedModule/utils/formatters";
import { hasTimeAdminAccess, hasTimeViewerAccess } from "../utils/timesUtils";
import {
  selectMetaTimesData,
  selectUserAssignees,
} from "../redux/reducers/metaTimes.reducer";
import {
  getIsoFormatStringWithTimeZone,
  getNextWeekDay,
  getPrevSunday,
} from "../../SharedModule/utils/dateUtils";
import { selectUserRoles } from "../../SharedModule/redux/reducers/auth.reducer";
import {
  hasSuperTimeAdminAccess,
  isSupervisorOrProjectOwner,
  isDeliveryTimeAdminChecker,
} from "../../SharedModule/utils/accessUtils";
import { actualUIState } from "../../SharedModule/redux/reducers/uiShared.reducer";
import { meal_break_status } from "../../SharedModule/utils/constants";

export const useTimes = (
  actualUserId: any,
  pageDate: Date,
  userEmail: string,
  entryReminderParam: boolean | null
) => {
  const dispatch = useDispatch();
  const isListView = useSelector(IsListView);
  const isLoading = useSelector(IsLoading);
  const isSummaryLoading = useSelector(IsSummaryLoading);
  const isRefreshing = useSelector(IsRefreshing);
  const hasInitiallyLoaded = useSelector(HasInitiallyLoaded);
  // Ref that mirrors hasInitiallyLoaded so the callback can read it without
  // listing it as a useCallback dep.  If hasInitiallyLoaded were a dep, the
  // callback would get a new reference the moment the first load completes
  // (false → true), causing useEffect to fire a second time and triggering
  // an unwanted second load.
  const hasInitiallyLoadedRef = useRef(hasInitiallyLoaded);
  hasInitiallyLoadedRef.current = hasInitiallyLoaded;
  const hasPageError = useSelector(HasPageError);
  const projectList = useSelector(ProjectList);
  const selectProjectOrWorkOrder = useSelector(SelectProjectOrWorkOrder);
  const timeEntriesList = useSelector(TimeEntriesList);
  const timeEntriesSheetView = useSelector(TimeEntriesSheetView);
  const summary = useSelector(Summary);
  const scheduledHours = useSelector(ScheduledHours);
  const holidays = useSelector(Holidays);
  const holidaysUserId = useSelector(HolidaysUserId);
  const holidaysRange = useSelector(HolidaysRange);
  // Same ref-mirror pattern as hasInitiallyLoadedRef above: read inside
  // loadHolidaysIfNeeded without making it a dep, so the callback's identity
  // stays stable across holidays fetches and doesn't cascade into
  // loadTimesPageInfo re-running on every load.
  const holidaysCacheRef = useRef({ userId: holidaysUserId, range: holidaysRange });
  holidaysCacheRef.current = { userId: holidaysUserId, range: holidaysRange };
  const entryList = useSelector(EntryList);
  const MetaTimesInfo = useSelector(selectMetaTimesData);
  const missingTime = useSelector(MissingTime);
  const missingTimeUserId = useSelector(MissingTimeUserId);
  const missingTimeMealBreak = useSelector(MissingTimeMealBreak);
  // Same ref-mirror pattern as holidaysCacheRef: MissingTimeService's result
  // doesn't depend on the viewed week, only on the user (and the calendar
  // date, which changes at most once a day) — so remindersChain skips
  // re-fetching it on plain week navigation when this already matches the
  // active user, and only forces a fresh fetch when forceReminderRefresh is
  // set (refreshPage, right after an entry is added/edited/deleted).
  const missingTimeCacheRef = useRef(missingTimeUserId);
  missingTimeCacheRef.current = missingTimeUserId;

  const { userRolesList } = useSelector(selectUserRoles);

  const isTimeAdmin = hasTimeAdminAccess(userRolesList);
  const isTimeViewer = hasTimeViewerAccess(userRolesList);
  const isSuperTimeAdmin = hasSuperTimeAdminAccess(userRolesList);
  const isSupervisorOrPO = isSupervisorOrProjectOwner(userRolesList);
  const isDeliveryTimeAdmin = isDeliveryTimeAdminChecker(userRolesList);

  const { entryReminder } = useSelector(actualUIState);

  let entryReminderCondition = entryReminderParam ?? entryReminder;

  // ASUITE11-2418: if user is Time Viewer and supervisor/project owner, we should use timeUsers (from query) intead of usersAssignees
  const users = MetaTimesInfo.timeUsers?.filter((user) => user.isActive);
  const usersAssignees = useSelector(selectUserAssignees);
  let usersForDropdown = isTimeViewer ? users : usersAssignees;

  // Chain H — company holidays (~fast, single query on a wide date window).
  // Decoupled from summary/entries: fetched once per user and cached in redux
  // (holidaysUserId/holidaysRange) instead of on every week navigation. Only
  // re-fetches when the user changes or when pageDate falls outside the
  // cached ±2-year window (e.g. jumping far via the calendar date picker).
  const loadHolidaysIfNeeded = useCallback(
    async (userId: string, referenceDate: Date) => {
      const cached = holidaysCacheRef.current;
      const isCached =
        cached.userId === userId &&
        !!cached.range &&
        referenceDate >= new Date(cached.range.start) &&
        referenceDate <= new Date(cached.range.end);

      if (isCached) return;

      const rangeStart = new Date(referenceDate);
      rangeStart.setFullYear(rangeStart.getFullYear() - 2);
      const rangeEnd = new Date(referenceDate);
      rangeEnd.setFullYear(rangeEnd.getFullYear() + 2);

      const startDate = getIsoFormatStringWithTimeZone(rangeStart);
      const endDate = getIsoFormatStringWithTimeZone(rangeEnd);

      const result = await TimesheetsService.getUserHolidays(
        userId,
        startDate,
        endDate
      )
        .then((data) => data.data)
        .catch(() => null);

      if (result?.data) {
        dispatch(
          setHolidays({
            holidays: result.data,
            userId,
            range: { start: startDate, end: endDate },
          })
        );
      }
    },
    [dispatch]
  );

  const loadTimesPageInfo = useCallback(
    async (pageDate: Date, forceReminderRefresh: boolean = false) => {
      if (actualUserId) {
        // Always set the entry form to the correct week date immediately so
        // the blank rows the user sees during loading already show the right
        // date — no stale date flash on first load or navigation.
        dispatch(resetEntries({ pageDate, hasContent: false }));
        // Reset once per load cycle. summaryChain/entriesChain below only ever
        // flip this back to true on failure — a successful chain never resets
        // it to false, so one chain failing can't be masked by the other
        // succeeding later (see setHasPageError in times.reducer.tsx).
        dispatch(setHasPageError(false));

        if (hasInitiallyLoadedRef.current) {
          // Stale-while-revalidate: keep isLoading=false so controls stay
          // enabled; skeleton rows appear via isRefreshing.
          dispatch(setRefreshing(true));
        } else {
          // First ever load: full spinner (isLoading=true).
          dispatch(setLoading(true));
        }

        const startDate = getIsoFormatStringWithTimeZone(getPrevSunday(pageDate));
        const endDate = getIsoFormatStringWithTimeZone(getNextWeekDay(6, pageDate));

        // Chain S — fast summary endpoint (~100 ms).
        // Calls /summary which runs a single SQL aggregation query (no full row
        // fetch).  Dispatches updateSummaryOnly to pre-populate the header totals
        // and scheduledHours WITHOUT dropping the spinner.  isLoading stays true
        // until loadTimesInfo fires below.
        // Returns employmentStatus so the reminder chain can decide which
        // reminder APIs to call without waiting for the slow entries endpoint.
        // This is also the only endpoint that still validates the user has a
        // Price Level set (GetUserTimeEntries no longer does) — a resolved
        // response with no data surfaces as hasPageError. A rejected promise
        // (network error, 401, 500, etc.) is a transport/auth failure, not
        // that signal, and is handled separately below.
        const summaryChain = async (): Promise<string | null> => {
          let summaryData: any;
          try {
            summaryData = await TimesheetsService.getTimeEntriesSummary(
              actualUserId,
              startDate,
              endDate
            ).then((data) => data.data);
          } catch {
            // Transport/auth failure (network error, 401, 500, etc.) — distinct
            // from the backend resolving with an explicit empty/error payload
            // for this user. Skip this load's summary rather than showing the
            // Price-Level-specific error for an unrelated failure.
            return null;
          }

          if (summaryData?.data) {
            dispatch(updateSummaryOnly(summaryData.data));
            return summaryData.data.user.employmentStatus ?? null;
          }

          dispatch(setHasPageError(true));
          return null;
        };

        // Chain R — reminder APIs, fires immediately after summaryChain so it
        // starts ~100 ms into the load rather than ~3 s (after full entries).
        // Uses employmentStatus from summaryChain to replicate the
        // ASUITE11-2781 meal-break gating without waiting for the entries endpoint.
        // Dispatches setReminderData so the banners can paint early — the
        // !isLoading gate has been removed from those banner conditions.
        const remindersChain = async (
          employmentStatus: string | null
        ): Promise<void> => {
          const mealBreakReminder = employmentStatus
            ? meal_break_status.includes(employmentStatus)
            : false;

          // MissingTimeService's result doesn't depend on the viewed week —
          // skip the HTTP call when we already have a value for this user and
          // nothing forced a refresh (see missingTimeCacheRef above).
          const missingTimeCached =
            !forceReminderRefresh &&
            missingTimeCacheRef.current === actualUserId;

          const missingTimePromise =
            entryReminderCondition && !missingTimeCached
              ? TimesheetsService.getMissingTime(userEmail)
              : Promise.resolve(undefined);

          const missingTimeMealBreakPromise = mealBreakReminder
            ? TimesheetsService.getMealBreakReminderInfo(userEmail)
            : Promise.resolve(null);

          const [missingTimeResult, missingTimeMealBreakInfo] =
            await Promise.all([
              missingTimePromise,
              missingTimeMealBreakPromise,
            ]);

          // undefined = cache hit, leave state.missingTime untouched (the
          // undefined-check guard in setReminderData preserves it).
          let missingTime;
          if (!entryReminderCondition) {
            missingTime = null;
          } else if (missingTimeCached) {
            missingTime = undefined;
          } else {
            missingTime = missingTimeResult;
          }

          dispatch(
            setReminderData({
              missingTime,
              missingTimeMealBreak: missingTimeMealBreakInfo,
              userId: actualUserId,
            })
          );
        };

        // Chain A — full entries endpoint (~3 s).
        // Fetches list view and sheet view only — user details and the header
        // summary now live exclusively in /summary (chain S above).
        // Dispatches loadTimesInfo which sets isLoading=false — this is the
        // moment the spinner drops and the full entry list renders.
        // Reminder data is handled by remindersChain above — omitting it here
        // so the loadTimesInfo reducer leaves missingTime/missingTimeMealBreak
        // untouched (the undefined-check guards in the reducer preserve them).
        const entriesChain = async () => {
          let timeEntries: any;
          try {
            timeEntries = await TimesheetsService.getTimesEntries(
              actualUserId,
              startDate,
              endDate
            ).then((data) => data.data);
          } catch {
            // Transport/auth failure — not the backend explicitly reporting no
            // data for this user. Skip this load rather than showing the
            // Price-Level-specific error for an unrelated failure.
            return;
          }

          if (!timeEntries?.data) {
            dispatch(setHasPageError(true));
            return;
          }

          dispatch(
            loadTimesInfo({
              timeEntriesInfo: timeEntries,
              pageDate: pageDate,
            })
          );
        };

        // Chain B — project list → dispatched independently so it never blocks
        // the entries + summary from rendering.
        const projectsChain = async () => {
          const projectsWorkOrders =
            await TimesheetsService.getUserProjects(actualUserId);
          const partial = projectsWorkOrders.data.map((elem) => ({
            value: elem.entityId,
            label: elem.showBudget
              ? elem.name +
                " (" +
                quantityFormat(elem.budgetRemaining) +
                " budget hours remaining)"
              : elem.name,
            categoryId: elem.categoryId,
            entityId: elem.entityId,
            isInternal: elem.isInternal,
            defaultTaskTypeId: elem.defaultTaskTypeId,
            taskTypesCategory: elem.taskTypesCategory,
          }));
          dispatch(setProjectList(partial));
        };

        // Execution order:
        //   summaryChain fires first (fast, ~100 ms), returns employmentStatus
        //   remindersChain fires immediately after summaryChain resolves
        //   entriesChain + projectsChain run fully in parallel with the above
        //   holidaysChain runs in parallel too, but short-circuits immediately
        //   when already cached for this user/date-range (see loadHolidaysIfNeeded)
        //
        // Net effect: reminder banners appear ~200–600 ms into the load
        // instead of ~7 s (previously blocked behind the full entries fetch).
        const summaryWithReminders = summaryChain().then(remindersChain);
        await Promise.all([
          summaryWithReminders,
          entriesChain(),
          projectsChain(),
          loadHolidaysIfNeeded(actualUserId, pageDate),
        ]);
      }
    },
    [dispatch, actualUserId, entryReminderParam, loadHolidaysIfNeeded]
  );

  useEffect(() => {
    loadTimesPageInfo(pageDate);
  }, [loadTimesPageInfo]);

  return {
    isLoading,
    isSummaryLoading,
    isRefreshing,
    hasPageError,
    isListView,
    projectList,
    selectProjectOrWorkOrder,
    timeEntriesList,
    timeEntriesSheetView,
    summary,
    scheduledHours,
    holidays,
    entryList,
    entryReminder,
    isTimeAdmin,
    isTimeViewer,
    isSuperTimeAdmin,
    isSupervisorOrPO,
    isDeliveryTimeAdmin,
    MetaTimesInfo,
    loadTimesPageInfo,
    missingTime,
    missingTimeMealBreak,
    usersForDropdown,
  };
};
